import React, { useState } from 'react';

import { LoadingButton } from '@mui/lab';
import { Button, Checkbox, FormControlLabel } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useContainer } from 'unstated-next';

import ClassesContainer from './ClassesContainer';
import { StyledLoadingButton } from '../../../../components/forms/LoadingButton';
import Modal from '../../../../components/Modal';
import { ClassStatus } from '../../../../context/types';
import { ClassContainer } from '../../../ClassModal/ClassContainer';

const FormActionContainer = styled.div`
    display: flex;
    align-items: center;
`;

const SaveDraftButton = styled(LoadingButton)`
    margin-right: 0.75rem;

    .MuiLoadingButton-loadingIndicatorCenter {
        color: ${(props) => props.theme.colors.primary.main};
    }
`;

const ConfirmTitle = styled.h2`
    margin: 0 0 1.5rem;
    font-weight: normal;
`;

const ConfirmText = styled.div`
    margin-bottom: 1rem;
`;

const ConfirmActions = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-top: 1rem;
`;

const StyledButton = styled(Button)`
    margin-left: 0.5rem;
`;

export interface ClassDetailEditFormActionsProps {}

const ClassDetailEditFormActions: React.FC<ClassDetailEditFormActionsProps> = () => {
    const { t } = useTranslation(['admin_settings', 'create_edit_class']);
    const { classRecord, createPublication, updateClass, getClass, handleSubmit, setErrorMessage } =
        useContainer(ClassContainer);
    const { fetchClasses } = useContainer(ClassesContainer);

    const { enqueueSnackbar } = useSnackbar();

    const [isPublishing, setIsPublishing] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [sendInvites, setSendInvites] = useState(true);

    const isDraft = classRecord?.status === ClassStatus.Draft;

    const onUpdateSuccess = async () => {
        enqueueSnackbar(t('admin_settings:classes.update-success'), { variant: 'success' });
        setErrorMessage('');
        await getClass();
        await fetchClasses();
    };

    const handleConfirmPublish = async () => {
        setOpenConfirmDialog(false);
        setIsPublishing(true);

        await updateClass({ onSuccess: () => {} }); // prevent the form from resetting
        await createPublication(sendInvites, { onSuccess: onUpdateSuccess });

        setIsPublishing(false);
    };

    const handlePublish = () => {
        setOpenConfirmDialog(true);
    };

    const handleUpdate = async () => {
        setIsUpdating(true);

        await updateClass({ onSuccess: onUpdateSuccess });

        setIsUpdating(false);
    };

    const handleCloseDialog = () => {
        setOpenConfirmDialog(false);
    };

    const handleCheckboxChange = (event) => {
        setSendInvites(event.target.checked);
    };

    if (isDraft) {
        return (
            <FormActionContainer>
                <SaveDraftButton
                    color="primary"
                    variant="outlined"
                    onClick={handleSubmit(handleUpdate)}
                    loading={isUpdating}
                    disabled={isUpdating}
                >
                    {t('create_edit_class:save-draft')}
                </SaveDraftButton>
                <StyledLoadingButton
                    color="primary"
                    variant="contained"
                    onClick={handleSubmit(handlePublish)}
                    loading={isPublishing}
                    disabled={isPublishing}
                >
                    {t('create_edit_class:schedule')}
                </StyledLoadingButton>

                <Modal isOpen={openConfirmDialog} onClose={handleCloseDialog}>
                    <ConfirmTitle>{t('create_edit_class:confirm-publish-title')}</ConfirmTitle>
                    <ConfirmText>{t('create_edit_class:confirm-publish-body')}</ConfirmText>
                    <FormControlLabel
                        control={<Checkbox checked={sendInvites} onChange={handleCheckboxChange} name="sendInvites" />}
                        label={t('create_edit_class:send-emails-label')}
                    />
                    <ConfirmActions>
                        <StyledButton onClick={handleCloseDialog} variant="outlined">
                            {t('create_edit_class:cancel')}
                        </StyledButton>
                        <StyledButton onClick={handleConfirmPublish} variant="contained" autoFocus>
                            {t('create_edit_class:schedule')}
                        </StyledButton>
                    </ConfirmActions>
                </Modal>
            </FormActionContainer>
        );
    }

    return (
        <FormActionContainer>
            <StyledLoadingButton
                color="primary"
                variant="contained"
                onClick={handleSubmit(handleUpdate)}
                loading={isUpdating}
                disabled={isUpdating}
            >
                {t('create_edit_class:save')}
            </StyledLoadingButton>
        </FormActionContainer>
    );
};

export default ClassDetailEditFormActions;
